'use client';

import React, { createContext, useState, useContext, useEffect } from 'react';
// import { useUserInfo } from './user-info-context';
import { useUser } from '@/lib/context/user-context';

interface Playlist {
  id: string;
  name: string;
  description?: string;
  episodes: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UserSettings {
  dictionarySetting?: string;
  languageSetting: string;
  targetLanguageSetting?: string;
  cefrLevel?: string;
  autoReadSetting: boolean;
  autoTranslateSetting: boolean;
  autoPlaySetting: boolean;
  autoTranslateLevel: string;
  myVocabularySetting: boolean;
  vocabularyDictionary?: string;
  favorites?: string[];
  playlists?: { [playlistId: string]: Playlist };
  subscribedPodcastIds?: string[];
  subscribedPodcastsSnapshot?: Record<string, {
    id: string;
    title: string;
    author?: string;
    imageUrl?: string;
    feedUrl?: string;
    defaultLevelRange?: string[] | string | null;
    updatedAt?: string;
  }>;
  preferChineseTitles?: boolean;
  personaProfile?: any | null;
}

interface UserSettingsContextType {
  userSettings: UserSettings;
  settings: UserSettings; // 添加别名方便访问
  personaProfile: UserSettings['personaProfile'];
  setUserSettings: React.Dispatch<React.SetStateAction<UserSettings>>;
  isLoading: boolean;
  refreshUserSettings: () => Promise<void>;
  hasLoaded: boolean;
}

const defaultSettings: UserSettings = {
  languageSetting: 'zh',
  cefrLevel: '',
  autoReadSetting: false,
  autoTranslateSetting: false,
  autoPlaySetting: false,
  autoTranslateLevel: '',
  myVocabularySetting: true,
  vocabularyDictionary: 'TOEFL',
  favorites: [],
  playlists: {},
  subscribedPodcastsSnapshot: {},
  preferChineseTitles: false,
  personaProfile: undefined
};

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return context;
};

export const UserSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  const [userSettings, setUserSettings] = useState<UserSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const CACHE_DURATION = 5000; // 5秒缓存
  const inFlightRef = React.useRef<Promise<void> | null>(null);
  
  // 添加 fetchCount 来追踪调用次数
  const fetchCount = React.useRef(0);

  const fetchUserSettings = React.useCallback(async (forceRefresh: boolean = false) => {
    if (!user?.id) {
      setUserSettings(defaultSettings);
      setIsLoading(false);
      setHasLoaded(true);
      return;
    }

    const now = Date.now();
    if (inFlightRef.current) {
      await inFlightRef.current;
      return;
    }
    if (!forceRefresh && now - lastFetchTime < CACHE_DURATION) {
      setHasLoaded(true);
      return;
    }

    setHasLoaded(false);
    const run = async () => {
      try {
      fetchCount.current += 1;
      // console.log("Fetching user settings for user:", user.id, forceRefresh ? "(force refresh)" : "");
      setIsLoading(true);

      // Web环境: 直接调用API
      const apiUrl = `/api/user-settings?userId=${encodeURIComponent(user.id)}`;
      console.log("Fetching settings from web API:", apiUrl);
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const settings = await response.json();
      console.log("Received settings from web API:", settings);
      
      setUserSettings(prev => ({
        ...prev,
        ...settings,
        personaProfile: typeof settings?.personaProfile === 'undefined'
          ? null
          : settings.personaProfile
      }));
      setLastFetchTime(now);
      } catch (error) {
        console.error('Error fetching user settings:', error);
      } finally {
        setIsLoading(false);
        setHasLoaded(true);
        inFlightRef.current = null;
      }
    };
    inFlightRef.current = run();
    await inFlightRef.current;
  }, [user?.id, lastFetchTime]);

  const refreshUserSettings = React.useCallback(async () => {
    await fetchUserSettings(true);
  }, [fetchUserSettings]);

  useEffect(() => {
    fetchUserSettings();
  }, [fetchUserSettings]); // 简化依赖

  const value = React.useMemo(() => ({
    userSettings,
    settings: userSettings, // 添加别名方便访问
    personaProfile: userSettings?.personaProfile,
    setUserSettings,
    isLoading,
    refreshUserSettings,
    hasLoaded
  }), [userSettings, isLoading, refreshUserSettings, hasLoaded]);

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
};
